import React, { useState } from 'react';
import { Input } from '@/ui-components/ui/input';
import { Button } from '@/ui-components/ui/button';
import { Label } from '@/ui-components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui-components/ui/popover';
import { Calendar } from '@/ui-components/ui/calendar';
import { Switch } from '@/ui-components/ui/switch';
import {
  X,
  Check,
  ImageIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { MediaType } from '@/page-components/post-generator/core/template.schemas';
import { AddImageDialog } from '@/page-components/post-generator/components/AddImageDialog';
import { useOrganizationsCustomerFindOneQuery } from '@/lib/api/requests/organizations.requests';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { cn } from '@/lib/utils/cn';

interface SelectionInputProps {
  value: any;
  onChange: (value: any, meta?: { width?: number; height?: number }) => void;
  mediaType: MediaType;
  className?: string;
  selectionId?: string;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

/**
 * Text Input Component
 */
export const TextInput: React.FC<SelectionInputProps> = ({ value, onChange, className, selectionId, onBlur, onKeyDown }) => {
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={selectionId ? `Text (${selectionId}) eingeben` : 'Text eingeben...'}
      className={cn("w-full h-9 sm:h-11 text-xs sm:text-sm border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all placeholder:truncate", className)}
    />
  );
};

/**
 * Date Input Component with optional time
 */
export const DateTimeInput: React.FC<SelectionInputProps> = ({ value, onChange, className, selectionId }) => {
  // Sicheres Parsen des Eingabewerts
  const parseInputValue = (inputValue: any): Date | undefined => {
    if (!inputValue) return undefined;
    
    try {
      const date = new Date(inputValue);
      return isNaN(date.getTime()) ? undefined : date;
    } catch {
      return undefined;
    }
  };

  const initialDate = parseInputValue(value);
  const [includeTime, setIncludeTime] = useState(() => {
    // Prüfe ob der Wert bereits eine Uhrzeit enthält
    if (initialDate) {
      return initialDate.getHours() !== 0 || initialDate.getMinutes() !== 0;
    }
    return false;
  });
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  const [timeValue, setTimeValue] = useState(() => {
    if (initialDate) {
      return format(initialDate, 'HH:mm');
    }
    return '12:00';
  });

  // Synchronisiere mit externen Wertänderungen
  React.useEffect(() => {
    const newDate = parseInputValue(value);
    setSelectedDate(newDate);
    
    if (newDate) {
      const hasTime = newDate.getHours() !== 0 || newDate.getMinutes() !== 0;
      setIncludeTime(hasTime);
      setTimeValue(format(newDate, 'HH:mm'));
    }
  }, [value]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    
    if (date) {
      // Erstelle eine neue Instanz um Mutationen zu vermeiden
      const newDate = new Date(date);
      
      if (includeTime) {
        const [hours, minutes] = timeValue.split(':');
        newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      } else {
        // Setze Zeit auf Mitternacht für reine Datumsauswahl
        newDate.setHours(0, 0, 0, 0);
      }
      
      onChange(newDate.toISOString());
    } else {
      onChange(null);
    }
  };

  const handleTimeChange = (time: string) => {
    setTimeValue(time);
    
    if (selectedDate) {
      const [hours, minutes] = time.split(':');
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      onChange(newDate.toISOString());
    }
  };

  const toggleTimeInclude = (checked: boolean) => {
    setIncludeTime(checked);
    
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      
      if (checked) {
        // Füge die aktuelle Uhrzeit hinzu
        const [hours, minutes] = timeValue.split(':');
        newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      } else {
        // Entferne die Uhrzeit (setze auf Mitternacht)
        newDate.setHours(0, 0, 0, 0);
      }
      
      onChange(newDate.toISOString());
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {/* Date Selection */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full h-9 sm:h-11 text-xs sm:text-sm justify-start"
            size="sm"
          >
            {selectedDate ? (
              format(selectedDate, includeTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy', { locale: de })
            ) : (
              selectionId ? `Datum (${selectionId}) wählen` : 'Datum wählen...'
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 space-y-2">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
              locale={de}
            />

          </div>
        </PopoverContent>
      </Popover>

      {/* Time Toggle and Input */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Switch
            checked={includeTime}
            onCheckedChange={toggleTimeInclude}
            className="scale-75"
            disabled={!selectedDate}
          />
          <Label className="text-xs text-gray-600">Uhrzeit</Label>
        </div>
        
        {includeTime && selectedDate && (
          <div className="flex items-center gap-1 flex-1">
            <Input
              type="time"
              value={timeValue}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="w-full h-9 sm:h-11 text-xs sm:text-sm border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all placeholder:truncate"
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Integer Input Component
 */
export const IntegerInput: React.FC<SelectionInputProps> = ({ value, onChange, className, selectionId, onBlur, onKeyDown }) => {
  const handleChange = (inputValue: string) => {
    const numValue = inputValue === '' ? null : parseInt(inputValue, 10);
    if (inputValue === '' || !isNaN(numValue!)) {
      onChange(numValue);
    }
  };

  return (
    <Input
      type="number"
      step="1"
      value={value ?? ''}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={selectionId ? `Ganzzahl (${selectionId}) eingeben` : 'Ganzzahl eingeben...'}
      className={cn("w-full h-9 sm:h-11 text-xs sm:text-sm border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all placeholder:truncate", className)}
    />
  );
};

/**
 * Float Input Component
 */
export const FloatInput: React.FC<SelectionInputProps> = ({ value, onChange, className, selectionId, onBlur, onKeyDown }) => {
  const [text, setText] = useState<string>(() => (
    value !== null && value !== undefined ? String(value).replace('.', ',') : ''
  ));
  const [isFocused, setIsFocused] = useState(false);

  React.useEffect(() => {
    if (!isFocused) {
      if (value === null || value === undefined) {
        setText('');
      } else {
        const num = typeof value === 'number' ? value : Number(value);
        if (!isNaN(num)) {
          const display = Number.isInteger(num)
            ? num.toFixed(2).replace('.', ',')
            : String(num).replace('.', ',');
          setText(display);
        } else {
          setText(String(value));
        }
      }
    }
  }, [value, isFocused]);

  const handleChange = (inputValue: string) => {
    setText(inputValue);
    const normalized = inputValue.replace(',', '.');
    const isValidPattern = /^-?\d*(?:[.,]\d*)?$/.test(inputValue);
    if (!isValidPattern) return;

    if (normalized === '') {
      onChange(null);
      return;
    }

    const isPartial = /^-?$/.test(inputValue) || /^-?[.,]$/.test(inputValue) || /^-?\d+[.,]$/.test(inputValue);
    if (isPartial) return;

    const parsed = parseFloat(normalized);
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={text}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        const t = text.trim();
        setIsFocused(false);
        if (t === '') {
          onChange(null);
          setText('');
          if (onBlur) onBlur();
          return;
        }
        const integerOnly = /^-?\d+$/.test(t);
        const endsWithSep = /^-?\d+[.,]$/.test(t);
        const hasDecimals = /^-?\d+[.,]\d+$/.test(t);
        let finalValue = null;
        if (integerOnly || endsWithSep) {
          const intDigits = t.replace(/[.,]$/, '');
          setText(`${intDigits},00`);
          finalValue = parseFloat(intDigits.replace(',', '.'));
        } else if (hasDecimals) {
          const normalized = t.replace(',', '.');
          setText(t.replace('.', ','));
          finalValue = parseFloat(normalized);
        } else {
          const normalized = t.replace(',', '.');
          const parsed = parseFloat(normalized);
          if (!isNaN(parsed)) {
            const hasSep = t.includes(',') || t.includes('.');
            setText(hasSep ? t.replace('.', ',') : `${t},00`);
            finalValue = parsed;
          }
        }
        if (finalValue !== null) onChange(finalValue);
        if (onBlur) onBlur();
      }}
      onChange={(e) => handleChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={selectionId ? `Kommazahl (${selectionId}) eingeben` : 'Kommazahl eingeben...'}
      className={cn("w-full h-9 sm:h-11 text-xs sm:text-sm border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all placeholder:truncate", className)}
    />
  );
};

/**
 * Color Input Component
 */
export const ColorInput: React.FC<SelectionInputProps> = ({ value, onChange, className, selectionId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isCustomer } = usePermissions();
  const { data: org } = useOrganizationsCustomerFindOneQuery({ enabled: isCustomer });
  
  // Vordefinierte Farbpalette
  const colorPresets = [
    '#001f3f', // Navy
    '#0074D9', // Blue
    '#2347BD', // Denim Blue
    '#7FDBFF', // Aqua
    '#39CCCC', // Teal
    '#B10DC9', // Purple
    '#F012BE', // Fuchsia
    '#85144b', // Maroon
    '#ed1c24', // Red
    '#FF851B', // Orange
    '#FFDC00', // Yellow
    '#3D9970', // Olive
    '#2ECC40', // Green
    '#29AF34', // Wageningen Green
    '#01FF70', // Lime
    '#111111', // Black
    '#AAAAAA', // Gray
    '#DDDDDD', // Silver
    '#FFFFFF', // White
  ];

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full h-9 sm:h-11 text-xs sm:text-sm justify-start"
            size="sm"
          >
            <div className="flex items-center gap-2 flex-1">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: value || '#000000' }}
              />
              <span className="flex-1 text-left">
                {value || (selectionId ? `Farbe (${selectionId}) wählen` : 'Farbe wählen...')}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3 z-[10000]" align="start">
          <div className="space-y-3">
            {/* Organization Colors - only for customers */}
            {isCustomer && (org?.primaryColor || org?.secondaryColor) && (
              <div className="space-y-2">
                <Label className="text-xs font-medium">Vereinsfarben</Label>
                <div className="flex gap-2">
                  {org.primaryColor && (
                    <button
                      onClick={() => handleColorSelect(org.primaryColor!)}
                      className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition-transform relative shadow-sm"
                      style={{ backgroundColor: org.primaryColor }}
                      title="Primärfarbe"
                    >
                      {value === org.primaryColor && (
                        <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow-md" />
                      )}
                    </button>
                  )}
                  {org.secondaryColor && (
                    <button
                      onClick={() => handleColorSelect(org.secondaryColor!)}
                      className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition-transform relative shadow-sm"
                      style={{ backgroundColor: org.secondaryColor }}
                      title="Sekundärfarbe"
                    >
                      {value === org.secondaryColor && (
                        <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow-md" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Custom Color Input */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Benutzerdefinierte Farbe</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={value || '#000000'}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-8 h-9 sm:h-11 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={value || '#000000'}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder="#000000"
                  className="flex-1 h-9 sm:h-11 text-xs sm:text-sm border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all placeholder:truncate"
                />
              </div>
            </div>

            {/* Color Presets */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Farbpalette</Label>
              <div className="grid grid-cols-5 gap-1">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    className="w-8 h-6 rounded border border-gray-300 hover:scale-110 transition-transform relative"
                    style={{ backgroundColor: color }}
                  >
                    {value === color && (
                      <Check className="w-3 h-3 text-white absolute inset-0 m-auto drop-shadow-sm" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * Image Input Component
 */
export const ImageInput: React.FC<SelectionInputProps> = ({ value, onChange, className, selectionId }) => {
  const { isCustomer } = usePermissions();
  const { data: org } = useOrganizationsCustomerFindOneQuery({ enabled: isCustomer });

  const displayUrl = React.useMemo(() => {
    // If we have a direct path (signed URL), use it
    if (value && value.path) return value.path;
    
    // Fallback: If we only have relativePath, try to find the matching signed URL from org
    if (isCustomer && value && value.relativePath && org?.logos && org?.logoUrls) {
      const index = org.logos.findIndex(l => l.path === value.relativePath);
      if (index !== -1 && org.logoUrls[index]) {
        return org.logoUrls[index];
      }
    }
    
    return null;
  }, [value, org, isCustomer]);

  const handleImageSelected = (path: string, id: string | null, displayName: string, width: number, height: number, previewUrl?: string, relativePath?: string) => {
    onChange({ mediaId: id || null, path: path, relativePath: relativePath || null }, { width, height });
  };

  return (
    <div className={cn("space-y-2", className)}>
      {displayUrl ? (
        <div className="relative w-full h-32 rounded-md overflow-hidden border border-gray-200 group">
          <img
            src={displayUrl}
            crossOrigin="anonymous"
            alt="Vorschau"
            className="w-full h-full object-contain bg-slate-50"
          />
        </div>
      ) : (
        <div className="flex items-center justify-center w-full h-32 rounded-md border-2 border-dashed border-gray-200 text-gray-400">
          <ImageIcon className="h-8 w-8" />
        </div>
      )}

      <AddImageDialog
        trigger={
          <Button
            type="button"
            variant="outline"
            className="w-full h-9 sm:h-11 text-xs sm:text-sm justify-center"
            size="sm"
          >
            {value && (value.path || value.relativePath) ? 'Bild ändern' : (selectionId ? `Bild (${selectionId}) auswählen` : 'Bild auswählen')}
          </Button>
        }
        onImageSelected={handleImageSelected}
      />
    </div>
  );
};

interface InputWrapperProps {
  children: React.ReactNode;
  value: any;
  onClear: () => void;
  mediaType: MediaType;
  className?: string;
  selectionId?: string;
}

const InputWrapper: React.FC<InputWrapperProps> = ({
  children,
  value,
  onClear,
  mediaType,
  className,
  selectionId
}) => {
  const hasValue = (() => {
    if (mediaType === 'image') {
      if (!value) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object') {
        const p = (value as any).path;
        const m = (value as any).mediaId;
        const hasPath = typeof p === 'string' && p.trim() !== '';
        const hasMediaId = typeof m === 'string' && m.trim() !== '';
        return hasPath || hasMediaId;
      }
      return false;
    }
    return value !== null && value !== undefined && value !== '';
  })();

  return (
    <div className={cn("flex items-center gap-2 sm:gap-4 group", className)} data-selection-id={selectionId ?? undefined}>

      {/* Input Field */}
      <div className="flex-1 relative min-w-0">
        {children}
      </div>

      {/* Action Button */}
      <div className="flex-shrink-0">
        {hasValue ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="h-8 w-8 sm:h-10 sm:w-10 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
          >
            <X className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        ) : (
          <div className="h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center">
            <div className="h-3 w-3 sm:h-4 sm:w-4 border-2 border-gray-300 rounded-full" />
          </div>
        )}
      </div>
    </div>
  );
};

export const SelectionStaticInputRenderer: React.FC<SelectionInputProps> = ({
  value,
  onChange,
  mediaType,
  className,
  selectionId,
  onBlur,
  onKeyDown
}) => {
  const baseClass = className ?? '';

  const handleClear = () => {
    onChange(null);
  };

  const renderInput = () => {
    switch (mediaType) {
      case 'text':
        return <TextInput value={value} onChange={onChange} mediaType={mediaType} className={baseClass} selectionId={selectionId} onBlur={onBlur} onKeyDown={onKeyDown} />;
      case 'date':
        return <DateTimeInput value={value} onChange={onChange} mediaType={mediaType} className={baseClass} selectionId={selectionId} />;
      case 'int':
        return <IntegerInput value={value} onChange={onChange} mediaType={mediaType} className={baseClass} selectionId={selectionId} onBlur={onBlur} onKeyDown={onKeyDown} />;
      case 'float':
        return <FloatInput value={value} onChange={onChange} mediaType={mediaType} className={baseClass} selectionId={selectionId} onBlur={onBlur} onKeyDown={onKeyDown} />;
      case 'color':
        return <ColorInput value={value} onChange={onChange} mediaType={mediaType} className={baseClass} selectionId={selectionId} />;
      case 'image':
        return <ImageInput value={value} onChange={onChange} mediaType={mediaType} className={baseClass} selectionId={selectionId} />;
      default:
        return <TextInput value={value} onChange={onChange} mediaType={mediaType} className={baseClass} selectionId={selectionId} onBlur={onBlur} onKeyDown={onKeyDown} />;
    }
  };

  return (
    <InputWrapper value={value} onClear={handleClear} mediaType={mediaType} className={className} selectionId={selectionId}>
      {renderInput()}
    </InputWrapper>
  );
};
